import { localeConfig, localeVarName } from "../config/locales.ts";
import { getProducts } from "./tangem.ts";
import type { Locale, LocaleGroup } from "../types";

export const fetchWithRetries = <T>(
  url: string,
  options: RequestInit = {},
  retries = 3,
): Promise<T> =>
  fetch(url, options).then((res) => {
    if (res.ok) {
      return res.json() as T;
    }
    if (retries > 0 && ![403, 404].includes(res.status)) {
      return fetchWithRetries<T>(url, options, retries - 1);
    }
    return res as T;
  });

export function addLocalesToProps(
  data: { params: { [p: string]: string | number }; props?: object }[],
) {
  if (Array.isArray(data)) {
    const groupsMap = Object.entries(localeConfig).reduce(
      (acc, [lang, { group }]) => {
        acc.set(group, (acc.get(group) || []).concat([lang as Locale]));
        return acc;
      },
      new Map<LocaleGroup, Locale[]>(),
    );

    return data
      .map(({ params, props }) => {
        const locale = params.locale;

        if (!localeConfig[locale]) return null;

        return {
          params,
          props: {
            ...props,
            locales: groupsMap.get(localeConfig[locale as Locale].group),
          },
        };
      })
      .filter((page) => Boolean(page));
  }
  return data;
}

export async function addPricesToProps(
  data: { params: { [p: symbol]: string } }[],
) {
  if (Array.isArray(data)) {
    const locales = data.map(({ params: { locale } }) => locale);
    let pricesData = {};
    if (locales.includes("ru")) {
      const ruPrices = await getProducts("ru");

      pricesData = {
        ...pricesData,
        ru: {
          ...ruPrices.prices,
          "TG115X2-S": ruPrices.prices.pack2,
          "TG115X3-S": ruPrices.prices.pack3,
          "TG130X3-B": { price: 7600 },
          "TG130X2-B": { price: 5800 },
        },
      };
    }

    const defaultPrices = {
      pack3g2: { price: 69.9, oldPrice: 69.9 },
      pack2g2: { price: 54.9, oldPrice: 54.9 },
    };

    return data.map(({ params, props }) => ({
      params,
      props: { ...props, prices: pricesData[params.locale] ?? defaultPrices },
    }));
  }
  return data;
}

export function selected(
  text: string,
  className: string,
  selectedClassName: string,
): string {
  const DELIMITER = "!!";
  const firstIndex = text.indexOf(DELIMITER);
  const lastIndex = text.lastIndexOf(DELIMITER);

  const textStart = firstIndex >= 0 ? text.substring(0, firstIndex) : "";
  const textSelected =
    firstIndex >= 0 && lastIndex > firstIndex
      ? text.substring(firstIndex + DELIMITER.length, lastIndex)
      : "";
  const textEnd =
    lastIndex >= 0 ? text.substring(lastIndex + DELIMITER.length) : text;
  let result = "";
  if (textStart.length > 0) {
    result = `<span class="${className}">${textStart}</span>`;
  }
  if (textSelected.length > 0) {
    result =
      result + `<span class="${selectedClassName}">${textSelected}</span>`;
  }
  if (textEnd.length > 0) {
    result = result + `<span class="${className}">${textEnd}</span>`;
  }
  return result;
}

export function getFormatPrice(locale: Locale, value: number) {
  const config = localeConfig[locale];

  const options = {
    style: "currency",
    minimumFractionDigits: config.fractionDigits,
    currency: config.currency,
  };

  return value.toLocaleString(config.locale, options);
}

export const isServer = !globalThis.window;

export function decodeTextFromLokalise(str: string) {
  return str
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");
}

export const deepClone = <T>(obj: T): T => {
  if ("structuredClone" in globalThis) {
    return globalThis.structuredClone(obj) as T;
  }
  return JSON.parse(JSON.stringify(obj)) as T;
};

export function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(";").shift() || null;
  }

  return null;
}
